import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";

type RiskGrade = {
  [key: string]: number;
};

const getRiskLabelAndColor = (score: number) => {
  switch (score) {
    case 5:
      return { label: "Low", textClass: "text-[#008000]", dotClass: "bg-[#008000]" };
    case 4:
      return { label: "Moderate", textClass: "text-[#47ca49]", dotClass: "bg-[#47ca49]" };
    case 3:
      return { label: "Medium", textClass: "text-[#a5ba56]", dotClass: "bg-[#a5ba56]" };
    case 2:
      return { label: "High", textClass: "text-red-400", dotClass: "bg-red-400" };
    case 1:
      return { label: "Severe", textClass: "text-red-500", dotClass: "bg-red-500" };
    default:
      return { label: "-", textClass: "text-gray-400", dotClass: "bg-gray-400" };
  }
};

const fieldLabels: Record<string, string> = {
  growth_score: "Growth Score",
  profitability_score: "Profitability Score",
  efficiency_score: "Efficiency Score",
  solvency_score: "Solvency Score",
  liquidity_score: "Liquidity Score",
};

export const RiskGradeSection = ({ riskGrade }: { riskGrade: RiskGrade }) => {
  return (
    <TooltipProvider>
      <section className="mb-8">
        <div className="flex gap-8">
          {/* Left side - Risk Grade Indicator */}
          <div className="flex-1">
            {/* Risk Grade Bar */}
            <div className="relative mb-3">
              <div className="flex h-8 rounded-lg overflow-hidden border">
              <div className="bg-[#008000] flex-1"></div>
              <div className="bg-[#47ca49] flex-1"></div>
              <div className="bg-[#f0983a] flex-1"></div>

                <div className="bg-red-400 flex-1"></div>
                <div className="bg-red-500 flex-1"></div>

              </div>
              <div
                className="absolute top-0 w-0.5 h-8 bg-black"
                style={{
                  right: `calc(${((riskGrade.overall_financial_score - 1) / 4) * 100}%)`,
                }}
              />
            </div>

            {/* Risk Labels */}
            <div className="flex justify-between text-sm text-gray-500 mb-4">
            <span>Low</span>

              <span>Moderate</span>
              <span>Medium</span>

              <span>High</span>

              <span>Severe</span>

            </div>

            {/* Overall Risk Level */}
            {riskGrade?.overall_financial_score && (
              <div className="text-center mb-4">
                <span
                  className={`text-2xl font-bold ${
                    getRiskLabelAndColor(riskGrade.overall_financial_score).textClass
                  }`}
                >
                  {getRiskLabelAndColor(riskGrade.overall_financial_score).label}
                </span>
              </div>
            )}

            <p className="text-sm text-gray-500 text-center">
              Indicates the company's financial strength and creditworthiness,
              derived by aggregating scores across key financial dimensions
              including Growth, Profitability, Liquidity, Solvency, and
              Efficiency.
            </p>
          </div>

          {/* Right side - Risk Parameters */}
          <div className="flex-1 space-y-4">
            {Object.entries(fieldLabels).map(([key, labelText]) => {
              const score = riskGrade[key];
              const { label, textClass, dotClass } = getRiskLabelAndColor(score);

              return (
                <div key={key} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{labelText}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                    </Tooltip>
                  </div>
                  <span className={`flex items-center gap-2 font-medium ${textClass}`}>
                    <span className={`w-2 h-2 rounded-full ${dotClass}`}></span>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </TooltipProvider>
  );
};
